import { useState } from 'react';

type SubmitStatus = 'idle' | 'success' | 'error';

const initialForm = {
  intent: 'rent', // 'rent' | 'buy'
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  productInterest: '',
  eventDate: '',
  eventLocation: '',
  guestCount: '',
  quantity: '',
  shipTo: '',
  message: '',
};

interface QuoteFormProps {
  theme?: 'dark' | 'light';
  onClose?: () => void;
}

export default function QuoteForm({ theme = 'dark', onClose }: QuoteFormProps) {
  const isDark = theme === 'dark';
  const [form, setForm] = useState(initialForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>('idle');

  const inputClass = isDark
    ? 'w-full bg-white/10 text-white placeholder-gray-500 border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent'
    : 'w-full bg-white text-gray-900 placeholder-gray-400 border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 focus:border-transparent';
  const selectClass = isDark
    ? 'w-full appearance-none bg-white/10 text-white border border-white/10 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 cursor-pointer'
    : 'w-full appearance-none bg-white text-gray-900 border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400 cursor-pointer';
  const labelClass = `block text-xs font-semibold uppercase tracking-wide mb-1.5 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const optionClass = isDark ? 'bg-gray-900' : '';
  const toggleInactiveClass = isDark
    ? 'bg-white/10 text-gray-300 border-white/10 hover:bg-white/20'
    : 'bg-white text-gray-600 border-gray-200 hover:border-teal-300 hover:text-teal-600';

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email) return;

    setIsSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(form).forEach(([k, v]) => body.append(k, v));

      const res = await fetch('https://readdy.ai/api/form/d6ug9l7b245v7h34obj0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) setForm(initialForm);
    } catch {
      setStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (status === 'success') {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 bg-teal-400/20 rounded-full flex items-center justify-center mx-auto mb-5">
          <i className="ri-check-line text-3xl text-teal-400"></i>
        </div>
        <h3 className={`text-xl font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Request Received!</h3>
        <p className={`text-sm leading-relaxed mb-6 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          Thank you! We&apos;ve received your request and will be in touch within 24 hours.
        </p>
        {onClose && (
          <button
            onClick={onClose}
            className="bg-teal-400 hover:bg-teal-500 text-white px-8 py-3 rounded-lg font-semibold text-sm transition-colors whitespace-nowrap cursor-pointer"
          >
            Close
          </button>
        )}
      </div>
    );
  }

  return (
    <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
      {/* Rent or Buy */}
      <div>
        <label className={labelClass}>
          Looking to <span className="text-teal-400">*</span>
        </label>
        <div className="grid grid-cols-2 gap-2">
          {(['rent', 'buy'] as const).map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => setForm((prev) => ({ ...prev, intent: opt }))}
              className={`rounded-lg px-4 py-2.5 text-sm font-semibold border transition-colors cursor-pointer ${form.intent === opt ? 'bg-teal-400 text-white border-teal-400' : toggleInactiveClass}`}
            >
              {opt === 'rent' ? 'Rent for an event' : 'Buy / purchase'}
            </button>
          ))}
        </div>
      </div>

      {/* Row 1: First + Last Name */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>
            First Name <span className="text-teal-400">*</span>
          </label>
          <input
            type="text"
            name="firstName"
            value={form.firstName}
            onChange={handleChange}
            required
            placeholder="Jane"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>
            Last Name <span className="text-teal-400">*</span>
          </label>
          <input
            type="text"
            name="lastName"
            value={form.lastName}
            onChange={handleChange}
            required
            placeholder="Smith"
            className={inputClass}
          />
        </div>
      </div>

      {/* Row 2: Email + Phone */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>
            Email Address <span className="text-teal-400">*</span>
          </label>
          <input
            type="email"
            name="email"
            value={form.email}
            onChange={handleChange}
            required
            placeholder="jane@example.com"
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Phone Number</label>
          <input
            type="tel"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            placeholder="+1 (555) 000-0000"
            className={inputClass}
          />
        </div>
      </div>

      {/* Product Interest */}
      <div>
        <label className={labelClass}>Product Interest</label>
        <div className="relative">
          <select name="productInterest" value={form.productInterest} onChange={handleChange} className={selectClass}>
            <option value="" className={optionClass}>Select a product</option>
            <option value="Standard Stretch Tents" className={optionClass}>Standard Stretch Tents</option>
            <option value="Custom Stretch Tents" className={optionClass}>Custom Stretch Tents</option>
            <option value="Custom Printed Skins" className={optionClass}>Custom Printed Skins</option>
            <option value="Bespoke Decor" className={optionClass}>Bespoke Decor</option>
          </select>
          <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
        </div>
      </div>

      {/* Rental-only fields */}
      {form.intent === 'rent' && (
        <>
          {/* Row 3: Event Date + Event Location */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Event Date</label>
              <input
                type="date"
                name="eventDate"
                value={form.eventDate}
                onChange={handleChange}
                className={`${inputClass} ${isDark ? '[color-scheme:dark]' : ''}`}
              />
            </div>
            <div>
              <label className={labelClass}>Event Location</label>
              <input
                type="text"
                name="eventLocation"
                value={form.eventLocation}
                onChange={handleChange}
                placeholder="City or venue name"
                className={inputClass}
              />
            </div>
          </div>

          {/* Guest Count */}
          <div>
            <label className={labelClass}>Expected Guest Count</label>
            <div className="relative">
              <select name="guestCount" value={form.guestCount} onChange={handleChange} className={selectClass}>
                <option value="" className={optionClass}>Select range</option>
                <option value="Under 50" className={optionClass}>Under 50</option>
                <option value="50-100" className={optionClass}>50–100</option>
                <option value="100-200" className={optionClass}>100–200</option>
                <option value="200-350" className={optionClass}>200–350</option>
                <option value="350-500" className={optionClass}>350–500</option>
                <option value="500+" className={optionClass}>500+</option>
              </select>
              <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"></i>
            </div>
          </div>
        </>
      )}

      {/* Purchase-only fields */}
      {form.intent === 'buy' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Quantity needed</label>
            <input
              type="text"
              name="quantity"
              value={form.quantity}
              onChange={handleChange}
              placeholder="e.g. 5 tents"
              className={inputClass}
            />
          </div>
          <div>
            <label className={labelClass}>Ship-to country</label>
            <input
              type="text"
              name="shipTo"
              value={form.shipTo}
              onChange={handleChange}
              placeholder="e.g. United States"
              className={inputClass}
            />
          </div>
        </div>
      )}

      {/* Message */}
      <div>
        <label className={labelClass}>
          Additional Message <span className="text-gray-500 normal-case">(Optional)</span>
        </label>
        <textarea
          name="message"
          value={form.message}
          onChange={handleChange}
          maxLength={500}
          rows={3}
          placeholder="Any additional details about your event..."
          className={`${inputClass} resize-none`}
        ></textarea>
      </div>

      {/* Error message */}
      {status === 'error' && (
        <p className={`text-xs ${isDark ? 'text-red-400' : 'text-red-600'}`}>
          Something went wrong. Please try again or email us at info@popprotents.com.
        </p>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full bg-teal-400 hover:bg-teal-500 text-white py-3.5 rounded-lg font-bold text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
      >
        {isSubmitting ? 'Submitting...' : form.intent === 'buy' ? 'Request Purchase Quote' : 'Request Rental Quote'}
      </button>

      {/* Privacy */}
      <p className={`text-center text-xs leading-relaxed ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
        By submitting this form you agree to our Privacy Policy. We will respond within 24 hours.
      </p>
    </form>
  );
}
